<template>
  <ContentLayout>
    <template #main>
      <div class="loadingArea" v-if="contentLoading">
        <span class="loader"></span>
      </div>
      <WrapFlowLayout>
        <li v-for="(item, key) of this.InlineFlowNumberChart" :key="key">
          <NumberChart :meta="item.meta" :data="item.data" />
        </li>
      </WrapFlowLayout>
      <hr />
      <WrapFlowLayout :minWidth="800" :gap="50" class="charts">
        <div class="chart">
          <TabSelect
            @change="(val) => monthSelectionChanged(val)"
            :tabs="['Heute', 'Woche', 'Monat', '3 Monate', 'Jahr']"
          />
          <apexchart
            type="bar"
            height="400"
            :options="chartOptions"
            :series="series"
          ></apexchart>
        </div>

        <ProjectMonthHeatmap
          :height="400"
          :series="heatmapSeries"
          :options="heatmapOptions"
        ></ProjectMonthHeatmap>
      </WrapFlowLayout>
    </template>
    <template #aside> </template>
  </ContentLayout>
</template>

<script>
import ContentLayout from "@/components/Layout/ContentLayout.vue";
import VueApexCharts from "vue3-apexcharts";
import ProjectMonthHeatmap from "@/components/ProjectMonthHeatmap.vue";
import WrapFlowLayout from "@/components/Layout/WrapFlowLayout.vue";
import TabSelect from "@/components/selectors/TabSelect.vue";
import NumberChart from "@/components/charts/NumberChart.vue";
import { DateTime } from "luxon";
import axios from "axios";

export default {
  name: "HomeView",
  components: {
    ContentLayout,
    apexchart: VueApexCharts,
    ProjectMonthHeatmap,
    WrapFlowLayout,
    TabSelect,
    NumberChart,
  },
  data() {
    return {
      dayOffsets: [],
      selectedIndex: 2,

      InlineFlowNumberChart: [
        { meta: { name: "Projekte", info: "Gesamt" }, data: "0" },
        { meta: { name: "Aktivitäten", info: "Heute" }, data: "0" },
        { meta: { name: "Fakturierbar", info: "Monat" }, data: "0" },
        { meta: { name: "Mitarbeiter", info: "Gesamt" }, data: "0" },
      ],
      contentLoading: false,
      chartOptions: {
        chart: {
          type: "bar",
          zoom: {
            enabled: true,
            resetIcon: {
              offsetX: -10,
              offsetY: 0,
              fillColor: "#888",
              strokeColor: "#37474F",
            },
            selection: {
              background: "#90CAF9",
              border: "#0D47A1",
            },
          },
          animations: {
            enabled: false,
          },
          toolbar: {
            show: true,
          },
          stroke: {
            curve: "straight",
          },
        },
        plotOptions: {
          bar: {
            distributed: true,
            borderRadius: 1,
            borderRadiusApplication: "around",
            borderRadiusWhenStacked: "last",
            columnWidth: "60%",
            dataLabels: {
              enabled: true,
              formatter: function (value) {
                return value + "h";
              },
              style: {
                fontSize: "14px",
                fontFamily: "Helvetica",
                colors: ["#fff"],
              },
            },
          },
        },
        colors: [],
        legend: {
          show: false,
          position: "top",
          fontSize: "16px",
          horizontalAlign: "center",
          floating: false,
          markers: {
            width: 15,
            height: 15,
            radius: 5,
          },
          itemMargin: {
            horizontal: 7,
            vertical: 7,
          },
          onItemHover: {
            highlightDataSeries: true,
          },
        },
        dataLabels: {
          enabled: false,
          formatter: function (value, { dataPointIndex, w }) {
            return w.config.xaxis.categories[dataPointIndex];
          },
          style: {
            fontSize: "14px",
            fontFamily: "Helvetica",
            colors: ["#FFF"],
          },
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: 0,
            show: true,
            hideOverflowingLabels: false,
            style: {
              fontSize: "12px",
              colors: ["#37474F"],
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              fontSize: "12px",
            },
          },
        },
      },
      series: [{ name: "Stunden", data: [] }],
      heatmapSeries: [],
      heatmapOptions: {
        categories: [...Array(12)].map((_, key) => key + 1),
        title: "Projekt Stunden",
        unit: "Stunden",
        colors: ["#f03030"],
      },
    };
  },
  mounted() {},
  created() {
    this.contentLoading = true;
    this.dayOffsets = this.generateOffsetArray();

    const fullyear = DateTime.now().minus({ year: 1 }).toISO().split("T")[0];
    axios.get("/api/projectsheatmap?start=" + fullyear).then((res) => {
      const data = res.data;
      this.heatmapSeries = data.map((item) => {
        return {
          ...item,
          data: item.data.map((value) => Math.floor(value / 60)),
        };
      });
      this.contentLoading = false;
    });

    this.monthSelectionChanged(this.selectedIndex);

    axios.get("/api/projects").then((res) => {
      this.InlineFlowNumberChart[0].data = res?.data?.length || "-";
    });
  },
  methods: {
    URLparams() {
      const [start, end] = this.offsetDays(this.dayOffsets[this.selectedIndex]);
      return `?start=${start}&end=${end}`;
    },

    offsetDays(days) {
      const start = DateTime.now()
        .minus({ days })
        .startOf("day")
        .toISO()
        .split("T")[0];
      const end = DateTime.now().endOf("day").toISO().split("T")[0];
      return [start, end];
    },
    generateOffsetArray() {
      const now = DateTime.now();
      return [
        0, // Heute
        Math.floor(now.diff(now.startOf("week"), "days").days), // Tage bis Anfang dieser Woche (Montag)
        Math.floor(now.diff(now.startOf("month"), "days").days), // Tage bis Anfang dieses Monats (1.)
        Math.floor(
          now.diff(now.minus({ months: 3 }).startOf("month"), "days").days
        ), // Tage bis Anfang des Monats vor 3 Monaten
        Math.floor(now.diff(now.startOf("year"), "days").days), // Tage bis Anfang des Jahres
      ];
    },
    monthSelectionChanged(to) {
      this.selectedIndex = to;
      this.series[0].data = [];
      this.chartOptions.xaxis.categories = [];

      const params = this.URLparams();

      this.contentLoading = true;
      axios.get("/api/data" + params).then((res) => {
        const data = res.data;
        console.log(data);
        for (let element of data) {
          this.series[0].data.push({
            x: element.project_name,
            y: element.total_hours,
            fillColor: element.color,
          });
          this.chartOptions.xaxis.categories.shift(element.project_name);
        }
        this.contentLoading = false;
      });
    },
  },
  computed: {},
};
</script>

<style scoped>
.chart {
  max-width: 100%;
  display: flex;
  flex-direction: column;
}

.loadingArea {
  position: absolute;
  z-index: 2;
  height: calc((10 / 11 * 100vh) - 25px);
  width: calc(100% - 25px);
  display: grid;
  place-content: center;
  background: rgba(255, 255, 255, 0.9);
}

.loader {
  width: 48px;
  height: 48px;
  border: 5px solid #f2275d;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: pulse 1s linear infinite;
}
.loader:after {
  content: "";
  position: absolute;
  width: 48px;
  height: 48px;
  border: 5px solid #f2275d;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  animation: scaleUp 1s linear infinite;
}

.charts > * {
  margin: 0 4em;
}

@keyframes scaleUp {
  0% {
    transform: translate(-50%, -50%) scale(0);
  }
  60%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}
@keyframes pulse {
  0%,
  60%,
  100% {
    transform: scale(1);
  }
  80% {
    transform: scale(1.2);
  }
}
</style>
